import React, { lazy, Suspense } from "react";
import { graphql } from "gatsby";

import Seo from "@/components/seo.jsx";
import NotificationToaster from "@/components/notification-toaster";
import FooterCopyrightText from "@/components/layout-blocks/footer/footer-copyright-text";
import LinkWrapper from "@/components/linkWrapper";

const Templates = {
  StrapiComponentArticleCentered: lazy(
    () => import("@/components/blocks/article/centered")
  ),

  StrapiComponentBlogThreeColumnCardsWithFilter: lazy(
    () => import("@/components/blocks/blog/three-column-cards-with-filter")
  ),
  StrapiComponentBlogThreeColumnCardsWithPagination: lazy(
    () => import("@/components/blocks/blog/three-column-cards-with-pagination")
  ),
  StrapiComponentBlogThreeColumnCards: lazy(
    () => import("@/components/blocks/blog/three-column-cards")
  ),
  StrapiComponentBlogThreeColumnWithBadges: lazy(
    () => import("@/components/blocks/blog/three-column-with-badges")
  ),
  StrapiComponentBlogTwoColumnWithSignUp: lazy(
    () => import("@/components/blocks/blog/two-column-with-sign-up")
  ),

  StrapiComponentCertificatesPentestCertificate: lazy(
    () => import("@/components/blocks/certificates/pentest-certificate")
  ),

  StrapiComponentContactCentered: lazy(
    () => import("@/components/blocks/contact/centered")
  ),

  StrapiComponentContentCentered: lazy(
    () => import("@/components/blocks/content/centered")
  ),
  StrapiComponentContentSplitWithImage: lazy(
    () => import("@/components/blocks/content/split-with-image")
  ),
  StrapiComponentContentTwoColumnsWithImage: lazy(
    () => import("@/components/blocks/content/two-columns-with-image")
  ),
  StrapiComponentContentTwoColumnsWithTestimonial: lazy(
    () => import("@/components/blocks/content/two-columns-with-testimonial")
  ),
  StrapiComponentContentTwoColumns: lazy(
    () => import("@/components/blocks/content/two-columns")
  ),
  StrapiComponentContentWithTestimonialAndStats: lazy(
    () => import("@/components/blocks/content/with-testimonial-and-stats")
  ),

  StrapiComponentCtaBrandPanelWithAppScreenshot: lazy(
    () => import("@/components/blocks/cta/brand-panel-with-app-screenshot")
  ),
  StrapiComponentCtaBrandPanelWithOverlappingImage: lazy(
    () => import("@/components/blocks/cta/brand-panel-with-overlapping-image")
  ),
  StrapiComponentCtaSimpleCenterBranded: lazy(
    () => import("@/components/blocks/cta/simple-center-branded")
  ),
  StrapiComponentCtaSimpleCenterWithImageBackground: lazy(
    () => import("@/components/blocks/cta/simple-center-with-image-background")
  ),
  StrapiComponentCtaSimpleCentered: lazy(
    () => import("@/components/blocks/cta/simple-centered")
  ),
  StrapiComponentCtaSimpleJustifiedOnLightBrand: lazy(
    () => import("@/components/blocks/cta/simple-justified-on-light-brand")
  ),
  StrapiComponentCtaSimpleJustified: lazy(
    () => import("@/components/blocks/cta/simple-justified")
  ),
  StrapiComponentCtaSimpleStacked: lazy(
    () => import("@/components/blocks/cta/simple-stacked")
  ),
  StrapiComponentCtaSplitWithImage: lazy(
    () => import("@/components/blocks/cta/split-with-image")
  ),

  StrapiComponentFaqCategoryHubFaq: lazy(
    () => import("@/components/blocks/faq/category-hub-faq")
  ),
  StrapiComponentFaqCenteredAccordion: lazy(
    () => import("@/components/blocks/faq/centered-accordion")
  ),
  StrapiComponentFaqOffsetWithSupportingText: lazy(
    () => import("@/components/blocks/faq/offset-with-supporting-text")
  ),
  StrapiComponentFaqSideBySide: lazy(
    () => import("@/components/blocks/faq/side-by-side")
  ),
  StrapiComponentFaqThreeColumns: lazy(
    () => import("@/components/blocks/faq/three-columns")
  ),
  StrapiComponentFaqTwoColumnsOnBrand: lazy(
    () => import("@/components/blocks/faq/two-columns-on-brand")
  ),
  StrapiComponentFaqTwoColumnsWithDescriptionOnDark: lazy(
    () => import("@/components/blocks/faq/two-columns-with-description-on-dark")
  ),
  StrapiComponentFaqTwoColumnsWithDescription: lazy(
    () => import("@/components/blocks/faq/two-columns-with-description")
  ),
  StrapiComponentFaqTwoColumns: lazy(
    () => import("@/components/blocks/faq/two-columns")
  ),

  StrapiComponentFeaturesCentered2X2Grid: lazy(
    () => import("@/components/blocks/features/centered-2x2-grid")
  ),
  StrapiComponentFeaturesGrid4X2OnBrand: lazy(
    () => import("@/components/blocks/features/grid-4x2-on-brand")
  ),
  StrapiComponentFeaturesGridWithOffsetIconsWithBigIcons: lazy(
    () =>
      import(
        "@/components/blocks/features/grid-with-offset-icons-with-big-icons"
      )
  ),
  StrapiComponentFeaturesGridWithOffsetIcons: lazy(
    () => import("@/components/blocks/features/grid-with-offset-icons")
  ),
  StrapiComponentFeaturesOffset2X2Grid: lazy(
    () => import("@/components/blocks/features/offset-2x2-grid")
  ),
  StrapiComponentFeaturesSimpleHeader: lazy(
    () => import("@/components/blocks/features/simple-header")
  ),
  StrapiComponentFeaturesSimpleThreeColumn: lazy(
    () => import("@/components/blocks/features/simple-three-column")
  ),
  StrapiComponentFeaturesWithFeatureGridList: lazy(
    () => import("@/components/blocks/features/with-feature-grid-list")
  ),
  StrapiComponentFeaturesWithFeatureList: lazy(
    () => import("@/components/blocks/features/with-feature-list")
  ),
  StrapiComponentFeaturesWithLargeScreenshot: lazy(
    () => import("@/components/blocks/features/with-large-screenshot")
  ),
  StrapiComponentFeaturesWithVariableImage: lazy(
    () => import("@/components/blocks/features/with-variable-image")
  ),

  StrapiComponentFeedSimpleWithDescriptionAlternating: lazy(
    () => import("@/components/blocks/feed/simple-with-description-alternating")
  ),
  StrapiComponentFeedSimpleWithDescription: lazy(
    () => import("@/components/blocks/feed/simple-with-description")
  ),

  StrapiComponentHeaderBrandedWithBackgroundImage: lazy(
    () => import("@/components/blocks/header/branded-with-background-image")
  ),
  StrapiComponentHeaderSimpleCentered: lazy(
    () => import("@/components/blocks/header/simple-centered")
  ),
  StrapiComponentHeaderSimpleWithSelectMenuDark: lazy(
    () => import("@/components/blocks/header/simple-with-select-menu-dark")
  ),
  StrapiComponentHeaderSimpleWithSelectMenu: lazy(
    () => import("@/components/blocks/header/simple-with-select-menu")
  ),
  StrapiComponentHeaderWithBackgroundImageAndOverlappingCards: lazy(
    () =>
      import(
        "@/components/blocks/header/with-background-image-and-overlapping-cards"
      )
  ),

  StrapiComponentHeroCardWithBackgroundImage: lazy(
    () => import("@/components/blocks/hero/card-with-background-image")
  ),
  StrapiComponentHeroDarkWithIllustration: lazy(
    () => import("@/components/blocks/hero/dark-with-illustration")
  ),
  StrapiComponentHeroImageCarousel: lazy(
    () => import("@/components/blocks/hero/image-carousel")
  ),
  StrapiComponentHeroSimpleCentered: lazy(
    () => import("@/components/blocks/hero/simple-centered")
  ),
  StrapiComponentHeroSplitWithNavbar: lazy(
    () => import("@/components/blocks/hero/split-with-navbar")
  ),
  StrapiComponentHeroSplitWithScreenshot: lazy(
    () => import("@/components/blocks/hero/split-with-screenshot")
  ),
  StrapiComponentHeroStaticBackgroundWithLogos: lazy(
    () => import("@/components/blocks/hero/static-background-with-logos")
  ),
  StrapiComponentHeroWithAngledImageOnRight: lazy(
    () => import("@/components/blocks/hero/with-angled-image-on-right")
  ),
  StrapiComponentHeroWithAppScreenshot: lazy(
    () => import("@/components/blocks/hero/with-app-screenshot")
  ),
  StrapiComponentHeroWithSignInForm: lazy(
    () => import("@/components/blocks/hero/with-sign-in-form")
  ),
  StrapiComponentHeroWithSignUpAndMediaContent: lazy(
    () => import("@/components/blocks/hero/with-sign-up-and-media-content")
  ),

  StrapiComponentLogoCloudOffWhiteGrid: lazy(
    () => import("@/components/blocks/logo-cloud/off-white-grid")
  ),
  StrapiComponentLogoCloudSimpleWithHeadingOnBrand: lazy(
    () => import("@/components/blocks/logo-cloud/simple-with-heading-on-brand")
  ),
  StrapiComponentLogoCloudSimpleWithHeading: lazy(
    () => import("@/components/blocks/logo-cloud/simple-with-heading")
  ),
  StrapiComponentLogoCloudSimple: lazy(
    () => import("@/components/blocks/logo-cloud/simple")
  ),
  StrapiComponentLogoCloudSplitWithGridOnRight: lazy(
    () => import("@/components/blocks/logo-cloud/split-with-grid-on-right")
  ),

  StrapiComponentNewsletterCenteredCardWithGraphic: lazy(
    () => import("@/components/blocks/newsletter/centered-card-with-graphic")
  ),
  StrapiComponentNewsletterSimpleStacked: lazy(
    () => import("@/components/blocks/newsletter/simple-stacked")
  ),
  StrapiComponentNewsletterSimpleWithDescription: lazy(
    () => import("@/components/blocks/newsletter/simple-with-description")
  ),
  StrapiComponentNewsletterWithDescriptionOnBrandCard: lazy(
    () =>
      import("@/components/blocks/newsletter/with-description-on-brand-card")
  ),
  StrapiComponentNewsletterWithParagraphOnBrandCard: lazy(
    () => import("@/components/blocks/newsletter/with-paragraph-on-brand-card")
  ),
  StrapiComponentNewsletterWithParagraphOnDark: lazy(
    () => import("@/components/blocks/newsletter/with-paragraph-on-dark")
  ),

  StrapiComponentShopProductDetailWithImageGrid: lazy(
    () => import("@/components/blocks/shop-product-detail/with-image-grid")
  ),

  StrapiComponentShopProductListSimpleProductGrid: lazy(
    () => import("@/components/blocks/shop-product-list/simple-product-grid")
  ),
  StrapiComponentShopProductListWithInlineActionsAndExpandableSidebarFilters:
    lazy(
      () =>
        import(
          "@/components/blocks/shop-product-list/with-inline-actions-and-expandable-sidebar-filters"
        )
    ),

  StrapiComponentStatsSimpleInCard: lazy(
    () => import("@/components/blocks/stats/simple-in-card")
  ),
  StrapiComponentStatsSimpleOnBrand: lazy(
    () => import("@/components/blocks/stats/simple-on-brand")
  ),
  StrapiComponentStatsSplitWithImage: lazy(
    () => import("@/components/blocks/stats/split-with-image")
  ),
  StrapiComponentStatsWithFadingBackgroundImage: lazy(
    () => import("@/components/blocks/stats/with-fading-background-image")
  ),

  StrapiComponentTeamDarkVersionWithLargeImages: lazy(
    () => import("@/components/blocks/team/dark-version-with-large-images")
  ),
  StrapiComponentTeamFullWidthWithVerticalImages: lazy(
    () => import("@/components/blocks/team/full-width-with-vertical-images")
  ),
  StrapiComponentTeamGridWithLargeRoundImages: lazy(
    () => import("@/components/blocks/team/grid-with-large-round-images")
  ),
  StrapiComponentTeamGridWithRoundImages: lazy(
    () => import("@/components/blocks/team/grid-with-round-images")
  ),
  StrapiComponentTeamWithImageAndShortParagraph: lazy(
    () => import("@/components/blocks/team/with-image-and-short-paragraph")
  ),
  StrapiComponentTeamWithLargeImages: lazy(
    () => import("@/components/blocks/team/with-large-images")
  ),
  StrapiComponentTeamWithSmallImages: lazy(
    () => import("@/components/blocks/team/with-small-images")
  ),
  StrapiComponentTeamWithVerticalImages: lazy(
    () => import("@/components/blocks/team/with-vertical-images")
  ),

  StrapiComponentTestimonialSideBySideOnBrand: lazy(
    () => import("@/components/blocks/testimonial/side-by-side-on-brand")
  ),
  StrapiComponentTestimonialSimpleCentered: lazy(
    () => import("@/components/blocks/testimonial/simple-centered")
  ),
  StrapiComponentTestimonialWithBackgroundImage: lazy(
    () => import("@/components/blocks/testimonial/with-background-image")
  ),
  StrapiComponentTestimonialWithLargeAvatar: lazy(
    () => import("@/components/blocks/testimonial/with-large-avatar")
  ),
  StrapiComponentTestimonialWithOverlappingImage: lazy(
    () => import("@/components/blocks/testimonial/with-overlapping-image")
  ),
};

export const query = graphql`
  query certificatesQuery(
    $locale: String
    $pageId: String
    $articleIds: [String]
    $authorIds: [String]
    $categoryIds: [String]
    $productIds: [String]
    $certificateIds: [String]
    $certificateTemplateId: String
  ) {
    page: strapiPage(id: { eq: $pageId }) {
      isTemplateFor
      components {
        __typename

        ...ArticleCenteredData

        ...BlogThreeColumnCardsWithFilterData
        ...BlogThreeColumnCardsWithPaginationData
        ...BlogThreeColumnCardsData
        ...BlogThreeColumnWithBadgesData
        ...BlogTwoColumnWithSignUpData

        ...ContactCenteredData

        ...ContentCenteredData
        ...ContentSplitWithImageData
        ...ContentTwoColumnsWithImageData
        ...ContentTwoColumnsWithTestimonialData
        ...ContentTwoColumnsData
        ...ContentWithTestimonialAndStatsData

        ...CtaBrandPanelWithAppScreenshotData
        ...CtaBrandPanelWithOverlappingImageData
        ...CtaSimpleCenterBrandedData
        ...CtaSimpleCenterWithImageBackgroundData
        ...CtaSimpleCenteredData
        ...CtaSimpleJustifiedOnLightBrandData
        ...CtaSimpleJustifiedData
        ...CtaSimpleStackedData
        ...CtaSplitWithImageData

        ...FaqCategoryHubFaqData
        ...FaqCenteredAccordionData
        ...FaqOffsetWithSupportingTextData
        ...FaqSideBySideData
        ...FaqThreeColumnsData
        ...FaqTwoColumnsOnBrandData
        ...FaqTwoColumnsWithDescriptionOnDarkData
        ...FaqTwoColumnsWithDescriptionData
        ...FaqTwoColumnsData

        ...FeaturesCentered2x2GridData
        ...FeaturesGrid4x2OnBrandData
        ...FeaturesGridWithOffsetIconsWithBigIconsData
        ...FeaturesGridWithOffsetIconsData
        ...FeaturesOffset2x2GridData
        ...FeaturesSimpleHeaderData
        ...FeaturesSimpleThreeColumnData
        ...FeaturesWithFeatureGridListData
        ...FeaturesWithFeatureListData
        ...FeaturesWithLargeScreenshotData
        ...FeaturesWithVariableImageData

        ...FeedSimpleWithDescriptionAlternatingData
        ...FeedSimpleWithDescriptionData

        ...HeaderBrandedWithBackgroundImageData
        ...HeaderSimpleCenteredData
        ...HeaderSimpleWithSelectMenuDarkData
        ...HeaderSimpleWithSelectMenuData
        ...HeaderWithBackgroundImageAndOverlappingCardsData

        ...HeroCardWithBackgroundImageData
        ...HeroDarkWithIllustrationData
        ...HeroImageCarouselData
        ...HeroSimpleCenteredData
        ...HeroSplitWithNavbarData
        ...HeroSplitWithScreenshotData
        ...HeroStaticBackgroundWithLogosData
        ...HeroWithAngledImageOnRightData
        ...HeroWithAppScreenshotData
        ...HeroWithSignInFormData
        ...HeroWithSignUpAndMediaContentData

        ...LogoCloudOffWhiteGridData
        ...LogoCloudSimpleWithHeadingOnBrandData
        ...LogoCloudSimpleWithHeadingData
        ...LogoCloudSimpleData
        ...LogoCloudSplitWithGridOnRightData

        ...NewsletterCenteredCardWithGraphicData
        ...NewsletterSimpleStackedData
        ...NewsletterSimpleWithDescriptionData
        ...NewsletterWithDescriptionOnBrandCardData
        ...NewsletterWithParagraphOnBrandCardData
        ...NewsletterWithParagraphOnDarkData

        ...ShopProductDetailWithImageGridData

        ...ShopProductListSimpleProductGridData

        ...StatsSimpleInCardData
        ...StatsSimpleOnBrandData
        ...StatsSplitWithImageData
        ...StatsWithFadingBackgroundImageData

        ...TeamDarkVersionWithLargeImagesData
        ...TeamFullWidthWithVerticalImagesData
        ...TeamGridWithLargeRoundImagesData
        ...TeamGridWithRoundImagesData
        ...TeamWithImageAndShortParagraphData
        ...TeamWithLargeImagesData
        ...TeamWithSmallImagesData
        ...TeamWithVerticalImagesData

        ...TestimonialSideBySideOnBrandData
        ...TestimonialSimpleCenteredData
        ...TestimonialWithBackgroundImageData
        ...TestimonialWithLargeAvatarData
        ...TestimonialWithOverlappingImageData

        ...CertificatesPentestCertificateData

        ... on StrapiComponentSharedSharedComponent {
          shared_component {
            components {
              __typename
              ...ArticleCenteredData

              ...BlogThreeColumnCardsWithFilterData
              ...BlogThreeColumnCardsWithPaginationData
              ...BlogThreeColumnCardsData
              ...BlogThreeColumnWithBadgesData
              ...BlogTwoColumnWithSignUpData

              ...ContactCenteredData

              ...ContentCenteredData
              ...ContentSplitWithImageData
              ...ContentTwoColumnsWithImageData
              ...ContentTwoColumnsWithTestimonialData
              ...ContentTwoColumnsData
              ...ContentWithTestimonialAndStatsData

              ...CtaBrandPanelWithAppScreenshotData
              ...CtaBrandPanelWithOverlappingImageData
              ...CtaSimpleCenterBrandedData
              ...CtaSimpleCenterWithImageBackgroundData
              ...CtaSimpleCenteredData
              ...CtaSimpleJustifiedOnLightBrandData
              ...CtaSimpleJustifiedData
              ...CtaSimpleStackedData
              ...CtaSplitWithImageData

              ...FaqCategoryHubFaqData
              ...FaqCenteredAccordionData
              ...FaqOffsetWithSupportingTextData
              ...FaqSideBySideData
              ...FaqThreeColumnsData
              ...FaqTwoColumnsOnBrandData
              ...FaqTwoColumnsWithDescriptionOnDarkData
              ...FaqTwoColumnsWithDescriptionData
              ...FaqTwoColumnsData

              ...FeaturesCentered2x2GridData
              ...FeaturesGrid4x2OnBrandData
              ...FeaturesGridWithOffsetIconsWithBigIconsData
              ...FeaturesGridWithOffsetIconsData
              ...FeaturesOffset2x2GridData
              ...FeaturesSimpleHeaderData
              ...FeaturesSimpleThreeColumnData
              ...FeaturesWithFeatureGridListData
              ...FeaturesWithFeatureListData
              ...FeaturesWithLargeScreenshotData
              ...FeaturesWithVariableImageData

              ...FeedSimpleWithDescriptionAlternatingData
              ...FeedSimpleWithDescriptionData

              ...HeaderBrandedWithBackgroundImageData
              ...HeaderSimpleCenteredData
              ...HeaderSimpleWithSelectMenuDarkData
              ...HeaderSimpleWithSelectMenuData
              ...HeaderWithBackgroundImageAndOverlappingCardsData

              ...HeroCardWithBackgroundImageData
              ...HeroDarkWithIllustrationData
              ...HeroImageCarouselData
              ...HeroSimpleCenteredData
              ...HeroSplitWithNavbarData
              ...HeroSplitWithScreenshotData
              ...HeroStaticBackgroundWithLogosData
              ...HeroWithAngledImageOnRightData
              ...HeroWithAppScreenshotData
              ...HeroWithSignInFormData
              ...HeroWithSignUpAndMediaContentData

              ...LogoCloudOffWhiteGridData
              ...LogoCloudSimpleWithHeadingOnBrandData
              ...LogoCloudSimpleWithHeadingData
              ...LogoCloudSimpleData
              ...LogoCloudSplitWithGridOnRightData

              ...NewsletterCenteredCardWithGraphicData
              ...NewsletterSimpleStackedData
              ...NewsletterSimpleWithDescriptionData
              ...NewsletterWithDescriptionOnBrandCardData
              ...NewsletterWithParagraphOnBrandCardData
              ...NewsletterWithParagraphOnDarkData

              ...ShopProductDetailWithImageGridData

              ...ShopProductListSimpleProductGridData

              ...StatsSimpleInCardData
              ...StatsSimpleOnBrandData
              ...StatsSplitWithImageData
              ...StatsWithFadingBackgroundImageData

              ...TeamDarkVersionWithLargeImagesData
              ...TeamFullWidthWithVerticalImagesData
              ...TeamGridWithLargeRoundImagesData
              ...TeamGridWithRoundImagesData
              ...TeamWithImageAndShortParagraphData
              ...TeamWithLargeImagesData
              ...TeamWithSmallImagesData
              ...TeamWithVerticalImagesData

              ...TestimonialSideBySideOnBrandData
              ...TestimonialSimpleCenteredData
              ...TestimonialWithBackgroundImageData
              ...TestimonialWithLargeAvatarData
              ...TestimonialWithOverlappingImageData

              ...CertificatesPentestCertificateData
            }
          }
        }
      }
    }
    layout: strapiLayout(locale: { eq: $locale }) {
      locale
      components {
        __typename
        ...FooterColumnsWithLeadingCompanyLogoData
        ...FooterFourColumnWithAddressData
        ...FooterFourColumnWithCompanyMissionData
        ...FooterFourColumnWithNewsletterAndLocalizationDarkData
        ...FooterFourColumnWithNewsletterAndLocalizationDarkData
        ...FooterFourColumnWithNewsletterDarkData
        ...FooterFourColumnWithNewsletterData
        ...FooterSimpleCenteredData
        ...FooterSocialLinksOnlyData

        ...NavbarCenteredLogoWithCartPopupData
        ...NavbarCenteredWithBottomBorderData
        ...NavbarSimpleBrandedWithStackedLinksData
        ...NavbarSimpleLinksCenteredData
        ...NavbarSimpleLinksOnLeftData
        ...NavbarWithFullWidthFlyoutsData

        ...HeroStaticBackgroundWithLogosData
      }
    }
    articles: allStrapiArticle(
      filter: { id: { in: $articleIds } }
      sort: { date: DESC }
    ) {
      nodes {
        ...ArticleCenteredArticle
        ...BlogThreeColumnCardsWithFilterArticle
        ...BlogThreeColumnCardsWithPaginationArticle
        ...BlogThreeColumnCardsArticle
        ...BlogThreeColumnWithBadgesArticle
        ...BlogTwoColumnWithSignUpArticle
      }
    }
    authors: allStrapiAuthor(filter: { id: { in: $authorIds } }) {
      nodes {
        id
      }
    }
    categories: allStrapiCategory(filter: { id: { in: $categoryIds } }) {
      nodes {
        ...BlogThreeColumnCardsWithFilterCategory
      }
    }
    products: allShopifyProcessedProduct(filter: { id: { in: $productIds } }) {
      nodes {
        ...ShopProductDetailWithImageGridProcessedProduct
        ...ShopProductListSimpleProductGridProcessedProduct
        ...ShopProductListWithInlineActionsAndExpandableSidebarFiltersProcessedProduct
      }
    }
    certificates: allStrapiCertificate(
      filter: { id: { in: $certificateIds } }
    ) {
      nodes {
        ...CertificatesPentestCertificateEntry
      }
    }
    certificateTemplate: strapiCertificateTemplate(
      id: { eq: $certificateTemplateId }
    ) {
      ...CertificatesPentestCertificateTemplateEntry
    }
  }
`;

const isObject = (o) => {
  return o && typeof o === "object" && !Array.isArray(o);
};

export default function Certificate({ data, pageContext, location }) {
  const pageData = data?.page || undefined;
  const layoutData = data?.layout || undefined;
  if (layoutData) {
    layoutData.componentsObject =
      layoutData.components?.reduce((obj, elem) => {
        return { ...obj, [elem.__typename]: elem };
      }, {}) || undefined;
  }
  const articles = data?.articles?.nodes || undefined;
  const authors = data?.authors?.nodes || undefined;
  const categories = data?.categories?.nodes || undefined;
  const products = data?.products?.nodes || undefined;
  const certificates = data?.certificates?.nodes || undefined;
  const certificateTemplate = data?.certificateTemplate || undefined;

  const components = [];
  for (let componentData of pageData?.components || []) {
    // merge layout data with component data
    if (layoutData?.componentsObject?.[componentData.__typename]) {
      // componentData clean up
      Object.keys(componentData).forEach((key) => {
        if (
          !componentData[key] ||
          (Array.isArray(componentData[key]) && !componentData[key]?.length) ||
          (isObject(componentData[key]) &&
            !Object.keys(componentData[key]).length)
        ) {
          delete componentData[key];
        }
      });
      // merge layoutData and pageData component
      componentData = Object.assign(
        {},
        layoutData?.componentsObject?.[componentData.__typename],
        componentData
      );
    }
    // add shared components
    if (componentData.__typename === "StrapiComponentSharedSharedComponent") {
      for (let sharedComponentData of componentData.shared_component
        ?.components || []) {
        let Template = Templates[sharedComponentData.__typename];
        if (Template) {
          components.push({
            name: sharedComponentData.__typename,
            react: (
              <Suspense fallback={null}>
                <Template
                  location={location}
                  locale={pageContext.locale}
                  data={sharedComponentData}
                  blogData={pageContext.blogData}
                  articles={articles}
                  authors={authors}
                  categories={categories}
                  shopData={pageContext.shopData}
                  products={products}
                  certificates={certificates}
                  certificateTemplateData={certificateTemplate}
                />
              </Suspense>
            ),
          });
        } else {
          // Warning Template nicht gefunden.
          console.warn(
            `No template found for shared component "${sharedComponentData.__typename}".`
          );
        }
      }
    } else {
      // add page components
      let Template = Templates[componentData.__typename];
      if (Template) {
        components.push({
          name: componentData.__typename,
          react: (
            <Suspense fallback={null}>
              <Template
                location={location}
                locale={pageContext.locale}
                data={componentData}
                blogData={pageContext.blogData}
                articles={articles}
                authors={authors}
                categories={categories}
                shopData={pageContext.shopData}
                products={products}
                certificates={certificates}
                certificateTemplateData={certificateTemplate}
              />
            </Suspense>
          ),
        });
      } else {
        // Warning Template nicht gefunden.
        console.warn(
          `No template found for component "${componentData.__typename}".`
        );
      }
    }
  }

  return (
    <>
      <NotificationToaster />
      <div className="flex min-h-screen flex-col bg-background-off-white">
        <div className="grow" />
        {components.map((component, index) => (
          <div key={index}>{component.react}</div>
        ))}
        <div className="mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-center border-t border-gray-400 pt-2">
            <FooterCopyrightText
              className="text-sm text-text-secondary"
              linkClassName="hover:text-text-primary"
              copyrightText={
                <span>
                  <LinkWrapper
                    to="https://turingpoint.de/"
                    className="hover:text-text-primary"
                  >
                    <b>turingpoint GmbH</b>
                  </LinkWrapper>
                  {". All rights reserved."}
                </span>
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}

export function Head({ pageContext }) {
  return (
    <>
      <html lang={pageContext.locale || "en"} />
      <Seo seoData={pageContext.seo} />
    </>
  );
}
